import { SxProps, Theme } from '@mui/material';

/**
 * The stack a card list sits in.
 *
 * Same rhythm as `myAppointments.styles.ts`'s `listStyles`, which solved this first: a
 * column of bordered rows with air between them, so one record reads as one thing.
 */
export const cardListStyles: SxProps<Theme> = {
    display: 'flex',
    flexDirection: 'column',
    gap: 1.5,
};

/**
 * One record.
 *
 * The surface is `myAppointments.styles.ts`'s `rowStyles` deliberately: a patient looking
 * at their appointments and a therapist looking at their patient list are looking at the
 * same kind of object, and it should not change shape between the two screens.
 *
 * `isMuted` is for a record that is still there but no longer live -- a deactivated clinic,
 * somebody whose invitation lapsed. Faded rather than hidden, because reactivating them is
 * done from this very card.
 */
export const cardStyles = (isMuted: boolean): SxProps<Theme> => ({
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
    p: 2,
    borderRadius: 2,
    border: '1px solid rgba(16,24,40,0.08)',
    backgroundColor: 'background.paper',
    opacity: isMuted ? 0.55 : 1,
});

/** The name, and whatever qualifies it -- a status chip, usually. */
export const cardHeaderStyles: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 1,
};

export const cardTitleStyles: SxProps<Theme> = {
    fontWeight: 700,
    // The one thing on the card that must survive a 320px screen whole.
    minWidth: 0,
    overflowWrap: 'anywhere',
};

/**
 * The fields, as label/value pairs.
 *
 * Two columns rather than a stacked label above every value: five stacked fields make a
 * card two thumbs tall, and these labels are one or two words. Both tracks are
 * `minmax(0, ...)` so neither can push the card wider than the screen -- without it a long
 * email is a horizontal scrollbar, which is the thing this whole exercise is removing.
 */
export const cardFieldsStyles: SxProps<Theme> = {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, auto) minmax(0, 1fr)',
    columnGap: 1.5,
    rowGap: 0.75,
    alignItems: 'baseline',
};

export const cardFieldLabelStyles: SxProps<Theme> = {
    color: 'text.secondary',
    fontSize: '0.8rem',
    fontWeight: 600,
};

/**
 * `anywhere` rather than `break-word`: an email address is one unbroken token, so nothing
 * short of breaking mid-word keeps `a.very.long.address@clinic.example` inside a 320px card.
 */
export const cardFieldValueStyles: SxProps<Theme> = {
    fontSize: '0.9rem',
    minWidth: 0,
    overflowWrap: 'anywhere',
};

/**
 * The row's actions.
 *
 * Left-aligned and wrapping, unlike the table's right-aligned cell: on a card there is no
 * column to align to, and a button pushed to the right edge of a phone reads as an
 * afterthought rather than as the thing the card is for.
 */
export const cardActionsStyles: SxProps<Theme> = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 1,
    mt: 0.5,
};
